import {ErrorType, NumberString, ResponseData} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useState} from 'react';
import BolProduct from '../../../../models/bolProductModel';
import {COUNTRIES} from '../../../../util/countryUtil';
import {ActivateHandle, VATType, VATValue} from '../../../../util/interfaces';
import {createErrorMessage} from '../../../../util/utils';
import {CustomVAT, DefaultVAT} from '../../../../util/vatUtil';
import {AppContext} from '../../../misc/appContext';
import ProductVAT from './productVAT';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    onSuccess: (product: BolProduct) => void;
    onEditDefaults?: VoidFunction;
}

function ProductVATController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {account} = useContext(AppContext).accountContext;
    const {t} = useTranslation();
    const {onSuccess, onEditDefaults} = props;
    const [active, setActive] = useState(false);
    const [product, setProduct] = useState(new BolProduct());
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, ErrorType>>({});
    const [availableCountries, setAvailableCountries] = useState<Array<[string, string]>>([]);

    function onActivate(product: BolProduct) {
        setProduct(product);
        setActive(true);
    }

    function onClickChange(countryCode: string, value: VATValue) {
        return function () {
            onChange(countryCode)(value);
        };
    }

    function onChange(countryCode: string) {
        return function (value: VATValue | number | string) {
            const _product = product.clone();
            const newValue: VATValue = typeof value === 'object' ? value : CustomVAT(Number(value) || 0);
            _product.vats.setCountryValue(countryCode, newValue);
            setProduct(_product);
            setErrors({...errors, [countryCode]: null});
        };
    }

    function onAddCountry(countryCode: string) {
        const _product = product.clone();
        _product.vats.setCountryValue(countryCode, DefaultVAT(0));
        setProduct(_product);
    }

    function onDeleteCountry(countryCode: string) {
        return function () {
            const _product = product.clone();
            _product.vats.deleteCountryValue(countryCode);
            setProduct(_product);
        };
    }

    function onChangeSplitPercentage(countryCode: string, oldPercentage: string) {
        return function (newPercentage: string) {
            const _product = product.clone();
            const currentValue = _product.vats.getCountryValue(countryCode);
            if (currentValue?.vat_type !== VATType.SPLIT) return;
            currentValue.value[newPercentage] = currentValue.value[oldPercentage];
            delete currentValue.value[oldPercentage];
            setProduct(_product);
        };
    }

    // The typed text is the value: keeping it in the VAT entry itself is what
    // lets a comma decimal survive, and leaves nothing to fall out of sync.
    function onChangeSplitValue(countryCode: string, percentage: string) {
        return function (value: string) {
            const _product = product.clone();
            const currentValue = _product.vats.getCountryValue(countryCode);
            if (currentValue?.vat_type !== VATType.SPLIT) return;
            currentValue.value[percentage] = value;
            setProduct(_product);
        };
    }

    function onClose() {
        setActive(false);
        setProduct(new BolProduct());
        setLoading(false);
        setErrors({});
        setAvailableCountries([]);
    }

    function onSubmit(retroactive?: boolean) {
        return function () {
            if (!account || !validateValues()) return;
            if (retroactive === undefined) {
                const message = t(
                    'settings.vat.productRetroactiveMsg',
                    'Wil je de gewijzigde BTW percentages met terugwerkende kracht toepassen? Hiermee worden alle facturen met dit product bijgewerkt.'
                );
                return showDialog(message, t('settings.vat.applyChanges', 'Wijzigingen toepassen'), [
                    {
                        label: t('settings.vat.noNewInvoicesOnly', 'Nee, alleen voor nieuwe facturen'),
                        onClick: onSubmit(false)
                    },
                    {
                        label: t('settings.vat.yesRetroactive', 'Ja, met terugwerkende kracht'),
                        onClick: onSubmit(true),
                        primary: false
                    }
                ]);
            }
            setLoading(true);
            parsedProduct().update(
                retroactive,
                (product: BolProduct, success: boolean, data: ResponseData) => {
                    setLoading(false);
                    if (success) {
                        onSuccess(product);
                        onClose();
                        showSnackbar(t('settings.vat.changesSaved', 'De wijzigingen zijn opgeslagen'));
                    } else if (!data.handled) showSaveError(data.message);
                },
                onUpdateFailed
            );
        };
    }

    function parsedProduct(): BolProduct {
        const _product = product.clone();
        for (const [, vatValue] of _product.vats.entries()) {
            if (vatValue.vat_type !== VATType.SPLIT) continue;
            for (const [percentage, value] of Object.entries(vatValue.value as Record<string, string | number>))
                vatValue.value[percentage] = splitAmount(value);
        }
        return _product;
    }

    function splitAmount(value: string | number): number {
        return new NumberString(value).numberValue;
    }

    function onUpdateFailed() {
        setLoading(false);
        showSaveError();
    }

    function showSaveError(message?: string | null) {
        showDialog(
            createErrorMessage(
                message ?? t('settings.vat.saveFailedMsg', 'Het opslaan van de wijzigingen is niet gelukt.')
            ),
            t('settings.vat.saveFailedTitle', 'Opslaan mislukt')
        );
    }

    function validateValues(): boolean {
        let valid = true;
        const _errors = {...errors};
        for (const [countryCode, vatValue] of product.vats.entries()) {
            if (![VATType.CUSTOM, VATType.SPLIT].includes(vatValue.vat_type)) continue;
            if (vatValue.vat_type === VATType.CUSTOM) {
                if (vatValue.value > 100) {
                    _errors[countryCode] = t(
                        'settings.vat.errMaxPercent',
                        'Het percentage kan niet meer zijn dan 100%'
                    );
                    valid = false;
                } else if (vatValue.value < 0) {
                    _errors[countryCode] = t(
                        'settings.vat.errNegativePercent',
                        'Het percentage kan niet negatief zijn'
                    );
                    valid = false;
                }
            } else if (vatValue.vat_type === VATType.SPLIT) {
                for (const value of Object.values(vatValue.value as Record<string, string | number>)) {
                    if (splitAmount(value) < 0) {
                        _errors[countryCode] = t(
                            'settings.vat.errNegativeAmount',
                            'Het BTW bedrag kan niet negatief zijn'
                        );
                        valid = false;
                    }
                }
            }
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    useEffect(() => {
        const usedCountryCodes = Array.from(product.vats.keys());
        setAvailableCountries(Object.entries(COUNTRIES).filter(entry => !usedCountryCodes.includes(entry[0])));
    }, [product.vats]);

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <ProductVAT
            onEditDefaults={onEditDefaults}
            onClose={onClose}
            onSubmit={onSubmit()}
            onClickChange={onClickChange}
            onChange={onChange}
            onAddCountry={onAddCountry}
            onDeleteCountry={onDeleteCountry}
            onChangeSplitPercentage={onChangeSplitPercentage}
            onChangeSplitValue={onChangeSplitValue}
            product={product}
            loading={loading}
            active={active}
            errors={errors}
            availableCountries={availableCountries}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(ProductVATController);
